import type { ResidentMedicationReadModel } from '@/services/medication-read.service';
import type { WoundAssessment } from '@/services/wound.service';
import { getContactsByResident, type ResidentContact } from '@/services/contacts.service';
import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';

// Das Morse-Sturzrisiko-Scoring-Feature wurde mit MED-323 entfernt (No-MDR,
// ADR-0001). Nur das menschliche fall_risk-Bool (Admission, Ja/Nein) bleibt
// Teil des Profils; die writer-losen Mirror-Spalten fall_risk_score/-level
// werden nicht mehr selektiert.
const CARE_RESIDENTS_SELECT = 'id, facility_id, first_name, last_name, date_of_birth, gender, room_number, station, ward, bed_number, care_level, primary_diagnosis, secondary_diagnoses, status, admission_date, allergies, fall_risk, wandering_risk, decubitus_risk, blood_type, chronic_conditions';
const RESIDENTS_SELECT = 'id, facility_id, first_name, last_name, date_of_birth, gender, room_number, ward, care_level, primary_diagnosis, secondary_diagnoses, status, admission_date, allergies, fall_risk, wandering_risk, decubitus_risk';

type ResidentTable = 'care_residents' | 'residents';

type ResidentPracticeLinkInfo = {
    patientId: string | null;
    practiceId: string | null;
    practiceName: string | null;
};

type CareAuthorizedPracticeLinkRow = {
    resident_id: string | null;
    patient_id: string | null;
    practice_id: string | null;
    practice_name: string | null;
};

type CareSharedAllergyRow = {
    resident_id: string | null;
    allergen: string | null;
    reaction: string | null;
    severity: string | null;
    confirmed_date: string | null;
};

type CareSharedDiagnosisRow = {
    resident_id: string | null;
    diagnosis: string | null;
};

type CareSharedBloodTypeRow = {
    resident_id: string | null;
    blood_type: string | null;
};

type CareSharedChronicConditionRow = {
    resident_id: string | null;
    condition_name: string | null;
    since_date: string | null;
};

type ResidentQueryOptions = {
    statuses?: Resident['status'][];
    search?: string;
    limit?: number;
};

export interface Resident {
    id: string;
    facilityId: string;
    firstName: string;
    lastName: string;
    room: string;       // maps to DB: room_number
    station: string;    // maps to DB: ward
    birthDate: string;  // maps to DB: date_of_birth
    admissionDate: string;
    status: 'active' | 'hospital' | 'vacation' | 'discharged';
    careLevel: number | null;
    allergies: string[];
    diagnoses: string[];
    primaryDiagnosis?: string;
    fallRisk?: boolean;
    wanderingRisk?: boolean;
    decubitusRisk?: string | null;
    gender?: string;
    bedNumber?: string | null;
    sharedAllergies: string[];
    sharedDiagnoses: string[];
    bloodType?: string | null;
    chronicConditions?: Array<{ condition: string; since?: string }>;
    sharedBloodType?: string | null;
    sharedChronicConditions?: Array<{ conditionName: string; sinceDate?: string }>;
    linkedPatientId?: string | null;
    linkedPracticeId?: string | null;
    linkedPracticeName?: string | null;
}

export interface ResidentProfile {
    id: string;
    facilityId: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    gender: string;
    roomNumber: string;
    ward: string;
    station: string;
    careLevel: number | null;
    primaryDiagnosis: string;
    secondaryDiagnoses: string[];
    status: Resident['status'];
    admissionDate: string;
    fallRisk: boolean;
    wanderingRisk: boolean;
    decubitusRisk: string | null;
    allergies: string[];
    bedNumber: string | null;
}

export interface ResidentAlertRecord {
    id: string;
    alertType: string;
    severity: 'critical' | 'warning' | 'info';
    title: string;
    message: string;
    createdAt: string;
    resolvedAt: string | null;
}

export interface ResidentChartReadModel {
    resident: ResidentProfile;
    medications: ResidentMedicationReadModel[];
    wounds: WoundAssessment[];
    alerts: ResidentAlertRecord[];
    contacts: ResidentContact[];
}

export interface ResidentChartReadOptions {
    facilityId?: string;
    include?: {
        medications?: boolean;
        wounds?: boolean;
        alerts?: boolean;
        contacts?: boolean;
    };
}

export interface CreateResidentInput {
    firstName: string;
    lastName: string;
    dateOfBirth?: string | null;
    gender?: string | null;
    roomNumber?: string | null;
    ward?: string | null;
    careLevel?: number | null;
    primaryDiagnosis?: string | null;
    admissionDate?: string | null;
    allergies?: string[];
    fallRisk?: boolean;
    secondaryDiagnoses?: string[];
    decubitusRisk?: string | null;
    status?: 'active' | 'hospital' | 'on_leave' | 'vacation' | 'discharged' | 'deceased';
}

function mapResidentStatus(status: string | null | undefined): Resident['status'] {
    if (status === 'hospital') return 'hospital';
    if (status === 'on_leave' || status === 'vacation') return 'vacation';
    if (status === 'discharged' || status === 'deceased') return 'discharged';
    return 'active';
}

function normalizeStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter(Boolean);
}

function mergeUniqueStrings(...values: Array<string[] | undefined>): string[] {
    const seen = new Set<string>();

    for (const group of values) {
        for (const entry of group ?? []) {
            const normalized = entry.trim();
            if (normalized) {
                seen.add(normalized);
            }
        }
    }

    return Array.from(seen);
}

function mapResidentRow(
    d: Record<string, unknown>,
    sharedAllergies: string[] = [],
    sharedDiagnoses: string[] = [],
    practiceLinkInfo?: ResidentPracticeLinkInfo,
    sharedBloodType: string | null = null,
    sharedChronicConditions: Array<{ conditionName: string; sinceDate?: string }> = [],
): Resident {
    const primaryDiagnosis = (d.primary_diagnosis as string) ?? sharedDiagnoses[0] ?? '';
    const diagnoses = mergeUniqueStrings(
        normalizeStringArray(d.secondary_diagnoses),
        sharedDiagnoses.filter((diagnosis) => diagnosis !== primaryDiagnosis),
    );

    return {
        id: d.id as string,
        facilityId: d.facility_id as string,
        firstName: d.first_name as string,
        lastName: d.last_name as string,
        room: (d.room_number as string) ?? '',
        station: ((d.station as string) ?? (d.ward as string) ?? ''),
        birthDate: (d.date_of_birth as string) ?? '',
        admissionDate: (d.admission_date as string) ?? '',
        status: mapResidentStatus(d.status as string | null | undefined),
        careLevel: d.care_level as number | null,
        allergies: mergeUniqueStrings(normalizeStringArray(d.allergies), sharedAllergies),
        diagnoses,
        primaryDiagnosis,
        fallRisk: (d.fall_risk as boolean) ?? false,
        wanderingRisk: (d.wandering_risk as boolean) ?? false,
        decubitusRisk: (d.decubitus_risk as string | null) ?? null,
        gender: (d.gender as string) ?? '',
        bedNumber: (d.bed_number as string | null) ?? null,
        sharedAllergies,
        sharedDiagnoses,
        bloodType: (d.blood_type as string | null) ?? sharedBloodType ?? null,
        chronicConditions: Array.isArray(d.chronic_conditions) ? (d.chronic_conditions as Array<{ condition: string; since?: string }>) : [],
        sharedBloodType: sharedBloodType ?? null,
        sharedChronicConditions,
        linkedPatientId: practiceLinkInfo?.patientId ?? null,
        linkedPracticeId: practiceLinkInfo?.practiceId ?? null,
        linkedPracticeName: practiceLinkInfo?.practiceName ?? null,
    };
}

function mapResidentToProfile(resident: Resident): ResidentProfile {
    return {
        id: resident.id,
        facilityId: resident.facilityId,
        firstName: resident.firstName,
        lastName: resident.lastName,
        dateOfBirth: resident.birthDate,
        gender: resident.gender ?? '',
        roomNumber: resident.room,
        ward: resident.station,
        station: resident.station,
        careLevel: resident.careLevel,
        primaryDiagnosis: resident.primaryDiagnosis ?? '',
        secondaryDiagnoses: resident.diagnoses,
        status: resident.status,
        admissionDate: resident.admissionDate,
        fallRisk: resident.fallRisk ?? false,
        wanderingRisk: resident.wanderingRisk ?? false,
        decubitusRisk: resident.decubitusRisk ?? null,
        allergies: resident.allergies,
        bedNumber: resident.bedNumber ?? null,
    };
}

async function getSharedAllergiesByResidentId(
    residentRows: Array<Record<string, unknown>>,
): Promise<Map<string, string[]>> {
    const residentIds = residentRows
        .map((row) => row.id)
        .filter((value): value is string => typeof value === 'string');

    if (residentIds.length === 0) {
        return new Map();
    }

    const { data, error } = await supabase
        .rpc('get_care_shared_allergies', { p_resident_ids: residentIds });

    if (error || !data) {
        if (error) {
            logger.warn('[resident.service] shared allergy rpc failed', { error: error.message });
        }
        return new Map();
    }

    const sharedAllergies = new Map<string, string[]>();

    for (const row of data as CareSharedAllergyRow[]) {
        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const allergen = typeof row.allergen === 'string' ? row.allergen.trim() : '';

        if (!residentId || !allergen) {
            continue;
        }

        sharedAllergies.set(
            residentId,
            mergeUniqueStrings(sharedAllergies.get(residentId), [allergen]),
        );
    }

    return sharedAllergies;
}

async function getSharedDiagnosesByResidentId(
    residentRows: Array<Record<string, unknown>>,
): Promise<Map<string, string[]>> {
    const residentIds = residentRows
        .map((row) => row.id)
        .filter((value): value is string => typeof value === 'string');

    if (residentIds.length === 0) {
        return new Map();
    }

    const { data, error } = await supabase
        .rpc('get_care_shared_diagnoses', { p_resident_ids: residentIds });

    if (error || !data) {
        if (error) {
            logger.warn('[resident.service] shared diagnoses rpc failed', { error: error.message });
        }
        return new Map();
    }

    const sharedDiagnoses = new Map<string, string[]>();

    for (const row of data as CareSharedDiagnosisRow[]) {
        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const diagnosis = typeof row.diagnosis === 'string' ? row.diagnosis.trim() : '';

        if (!residentId || !diagnosis) {
            continue;
        }

        sharedDiagnoses.set(
            residentId,
            mergeUniqueStrings(sharedDiagnoses.get(residentId), [diagnosis]),
        );
    }

    return sharedDiagnoses;
}

async function getSharedBloodTypeByResidentId(
    residentRows: Array<Record<string, unknown>>,
): Promise<Map<string, string>> {
    const residentIds = residentRows
        .map((row) => row.id)
        .filter((value): value is string => typeof value === 'string');

    if (residentIds.length === 0) {
        return new Map();
    }

    const { data, error } = await supabase
        .rpc('get_care_shared_blood_type', { p_resident_ids: residentIds });

    if (error || !data) {
        if (error) {
            logger.warn('[resident.service] shared blood type rpc failed', { error: error.message });
        }
        return new Map();
    }

    const bloodTypes = new Map<string, string>();

    for (const row of data as CareSharedBloodTypeRow[]) {
        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const bloodType = typeof row.blood_type === 'string' ? row.blood_type.trim() : '';

        if (!residentId || !bloodType || bloodTypes.has(residentId)) {
            continue;
        }

        bloodTypes.set(residentId, bloodType);
    }

    return bloodTypes;
}

async function getSharedChronicConditionsByResidentId(
    residentRows: Array<Record<string, unknown>>,
): Promise<Map<string, Array<{ conditionName: string; sinceDate?: string }>>> {
    const residentIds = residentRows
        .map((row) => row.id)
        .filter((value): value is string => typeof value === 'string');

    if (residentIds.length === 0) {
        return new Map();
    }

    const { data, error } = await supabase
        .rpc('get_care_shared_chronic_conditions', { p_resident_ids: residentIds });

    if (error || !data) {
        if (error) {
            logger.warn('[resident.service] shared chronic conditions rpc failed', { error: error.message });
        }
        return new Map();
    }

    const conditions = new Map<string, Array<{ conditionName: string; sinceDate?: string }>>();

    for (const row of data as CareSharedChronicConditionRow[]) {
        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const conditionName = typeof row.condition_name === 'string' ? row.condition_name.trim() : '';

        if (!residentId || !conditionName) {
            continue;
        }

        const current = conditions.get(residentId) ?? [];
        if (!current.some((entry) => entry.conditionName === conditionName)) {
            current.push({
                conditionName,
                sinceDate: typeof row.since_date === 'string' ? row.since_date : undefined,
            });
        }
        conditions.set(residentId, current);
    }

    return conditions;
}

async function getLinkedPracticeInfoByResidentId(
    residentRows: Array<Record<string, unknown>>,
): Promise<Map<string, ResidentPracticeLinkInfo>> {
    const residentIds = residentRows
        .map((row) => row.id)
        .filter((value): value is string => typeof value === 'string');

    if (residentIds.length === 0) {
        return new Map();
    }

    const { data, error } = await supabase
        .rpc('get_care_authorized_practice_links', { p_resident_ids: residentIds });

    if (error || !data || data.length === 0) {
        if (error) {
            logger.warn('[resident.service] linked practice lookup failed', { error: error.message });
        }
        return new Map();
    }

    const residentLinks = new Map<string, ResidentPracticeLinkInfo>();

    for (const row of data as CareAuthorizedPracticeLinkRow[]) {
        const residentId = typeof row.resident_id === 'string' ? row.resident_id : null;
        const practiceId = typeof row.practice_id === 'string' ? row.practice_id : null;

        if (!residentId || residentLinks.has(residentId)) {
            continue;
        }

        residentLinks.set(residentId, {
            patientId: typeof row.patient_id === 'string' ? row.patient_id : null,
            practiceId,
            practiceName: typeof row.practice_name === 'string' ? row.practice_name : null,
        });
    }

    return residentLinks;
}

// ─── Resident-Link Facade ───────────────────────────────────────────────────

export interface ResidentDoctorInfo {
    name: string;
    phone: string | null;
    source: 'contact' | 'emergency_sheet';
}

export interface ResidentLinkInfo {
    practiceId: string | null;
    practiceName: string | null;
    patientId: string | null;
    doctors: ResidentDoctorInfo[];
}

/**
 * Aggregiert alle verfügbaren Arzt-/Praxis-Informationen für einen einzelnen Bewohner:
 * 1. Verknüpfte Praxis aus `get_care_authorized_practice_links`
 * 2. Arztkontakte aus `resident_contacts` (contact_type = 'doctor')
 * 3. Behandelnder Arzt aus dem Notfallblatt
 */
export async function getResidentLinkInfo(residentId: string): Promise<ResidentLinkInfo> {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(residentId)) {
        return { practiceId: null, practiceName: null, patientId: null, doctors: [] };
    }

    const { data: linkData, error: linkError } = await supabase
        .rpc('get_care_authorized_practice_links', { p_resident_ids: [residentId] });

    if (linkError) {
        logger.warn('[resident.service] getResidentLinkInfo link lookup failed', { error: linkError.message });
    }

    const linkRow = (linkData as CareAuthorizedPracticeLinkRow[] | null)?.find(
        (r) => r.resident_id === residentId,
    );

    const practiceId = typeof linkRow?.practice_id === 'string' ? linkRow.practice_id : null;
    const practiceName = typeof linkRow?.practice_name === 'string' ? linkRow.practice_name : null;
    const patientId = typeof linkRow?.patient_id === 'string' ? linkRow.patient_id : null;

    const doctors: ResidentDoctorInfo[] = [];
    const seenNames = new Set<string>();

    // Arztkontakte aus resident_contacts
    try {
        const contacts = await getContactsByResident(residentId);
        for (const c of contacts) {
            if (c.contactType === 'doctor' && c.name && !seenNames.has(c.name)) {
                seenNames.add(c.name);
                doctors.push({ name: c.name, phone: c.phone ?? c.mobile ?? null, source: 'contact' });
            }
        }
    } catch {
        logger.warn('[resident.service] getResidentLinkInfo contacts lookup failed');
    }

    // Behandelnder Arzt aus Notfallblatt
    try {
        const { data: esData } = await supabase
            .from('care_emergency_sheets')
            .select('treating_physician_name, treating_physician_phone')
            .eq('resident_id', residentId)
            .eq('is_current', true)
            .maybeSingle();

        const esRow = esData as Record<string, unknown> | null;
        const physicianName = typeof esRow?.treating_physician_name === 'string' ? esRow.treating_physician_name.trim() : null;
        if (physicianName && !seenNames.has(physicianName)) {
            seenNames.add(physicianName);
            doctors.push({
                name: physicianName,
                phone: typeof esRow?.treating_physician_phone === 'string' ? esRow.treating_physician_phone : null,
                source: 'emergency_sheet',
            });
        }
    } catch {
        logger.warn('[resident.service] getResidentLinkInfo emergency sheet lookup failed');
    }

    return { practiceId, practiceName, patientId, doctors };
}

function mergeResidentRows(
    rows: Record<string, unknown>[],
    sharedAllergies = new Map<string, string[]>(),
    sharedDiagnoses = new Map<string, string[]>(),
    practiceLinks = new Map<string, ResidentPracticeLinkInfo>(),
    sharedBloodTypes = new Map<string, string>(),
    sharedChronicConditions = new Map<string, Array<{ conditionName: string; sinceDate?: string }>>(),
): Resident[] {
    const merged = new Map<string, Resident>();

    for (const row of rows) {
        const residentId = row.id as string;
        const mapped = mapResidentRow(
            row,
            sharedAllergies.get(residentId) ?? [],
            sharedDiagnoses.get(residentId) ?? [],
            practiceLinks.get(residentId),
            sharedBloodTypes.get(residentId) ?? null,
            sharedChronicConditions.get(residentId) ?? [],
        );
        const existing = merged.get(mapped.id);
        merged.set(mapped.id, existing ? { ...existing, ...mapped } : mapped);
    }

    return Array.from(merged.values()).sort((left, right) => {
        const lastNameCompare = left.lastName.localeCompare(right.lastName, 'de');
        if (lastNameCompare !== 0) return lastNameCompare;
        return left.firstName.localeCompare(right.firstName, 'de');
    });
}

function applyResidentFilters(residents: Resident[], options?: ResidentQueryOptions): Resident[] {
    const normalizedSearch = options?.search?.trim().toLowerCase();

    const filtered = residents.filter((resident) => {
        if (options?.statuses?.length && !options.statuses.includes(resident.status)) {
            return false;
        }

        if (!normalizedSearch) {
            return true;
        }

        return [resident.firstName, resident.lastName, resident.room, resident.station]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(normalizedSearch));
    });

    if (!options?.limit) {
        return filtered;
    }

    return filtered.slice(0, options.limit);
}

async function fetchResidentAlerts(residentId: string): Promise<ResidentAlertRecord[]> {
    const { data, error } = await supabase
        .from('care_alerts')
        .select('id, alert_type, severity, title, message, created_at, resolved_at')
        .eq('resident_id', residentId)
        .order('created_at', { ascending: false })
        .limit(20);

    if (error) {
        throw new Error(error.message);
    }

    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
        id: row.id as string,
        alertType: (row.alert_type as string) ?? 'other',
        severity: ((row.severity as ResidentAlertRecord['severity']) ?? 'info'),
        title: (row.title as string) ?? '',
        message: (row.message as string) ?? '',
        createdAt: (row.created_at as string) ?? '',
        resolvedAt: (row.resolved_at as string | null) ?? null,
    }));
}

async function fetchResidentMedications(residentId: string): Promise<ResidentMedicationReadModel[]> {
    const { getResidentMedicationReadModels } = await import('@/services/medication-read.service');
    return getResidentMedicationReadModels(residentId);
}

async function fetchResidentWounds(facilityId: string, residentId: string): Promise<WoundAssessment[]> {
    const { getWounds } = await import('@/services/wound.service');
    return getWounds(facilityId, { residentId });
}

async function fetchResidentsByFacility(table: ResidentTable, facilityId: string): Promise<Record<string, unknown>[]> {
    const query = table === 'care_residents'
        ? supabase
            .from('care_residents')
            .select(CARE_RESIDENTS_SELECT)
            .eq('facility_id', facilityId)
            .is('deleted_at', null)
            .order('last_name')
        : supabase
            .from('residents')
            .select(RESIDENTS_SELECT)
            .eq('facility_id', facilityId)
            .order('last_name');

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []) as Record<string, unknown>[];
}

async function fetchResidentsByIds(table: ResidentTable, ids: string[]): Promise<Record<string, unknown>[]> {
    if (ids.length === 0) return [];

    const query = table === 'care_residents'
        ? supabase
            .from('care_residents')
            .select(CARE_RESIDENTS_SELECT)
            .in('id', ids)
            .is('deleted_at', null)
        : supabase
            .from('residents')
            .select(RESIDENTS_SELECT)
            .in('id', ids);

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []) as Record<string, unknown>[];
}

async function collectResidentRows(
    lookup: { facilityId: string } | { ids: string[] },
): Promise<Record<string, unknown>[]> {
    const loadRows = async (table: ResidentTable): Promise<Record<string, unknown>[]> => {
        try {
            return 'facilityId' in lookup
                ? await fetchResidentsByFacility(table, lookup.facilityId)
                : await fetchResidentsByIds(table, lookup.ids);
        } catch (error) {
            logger.warn('[resident.service] resident table read failed, continuing with fallback', {
                table,
                error: error instanceof Error ? error.message : 'unknown',
            });
            return [];
        }
    };

    const [legacyRows, careRows] = await Promise.all([
        loadRows('residents'),
        loadRows('care_residents'),
    ]);

    if (legacyRows.length === 0 && careRows.length === 0) {
        throw new Error('Keine Bewohnerdaten verfuegbar.');
    }

    return [...legacyRows, ...careRows];
}

async function getPreferredResidentTable(facilityId: string): Promise<ResidentTable> {
    const [{ count: careCount, error: careError }, { count: residentsCount, error: residentsError }] = await Promise.all([
        supabase
            .from('care_residents')
            .select('id', { count: 'exact', head: true })
            .eq('facility_id', facilityId)
            .is('deleted_at', null),
        supabase
            .from('residents')
            .select('id', { count: 'exact', head: true })
            .eq('facility_id', facilityId),
    ]);

    if (careError && residentsError) {
        throw new Error(residentsError.message);
    }

    if (careError) {
        logger.warn('[resident.service] care_residents count failed, falling back to residents', {
            error: careError.message,
        });
        return 'residents';
    }

    if (residentsError) {
        logger.warn('[resident.service] residents count failed, falling back to care_residents', {
            error: residentsError.message,
        });
        return 'care_residents';
    }

    const safeCareCount = careCount ?? 0;
    const safeResidentsCount = residentsCount ?? 0;

    if (safeResidentsCount > safeCareCount) {
        return 'residents';
    }

    return 'care_residents';
}

async function insertResidentRecord(
    table: ResidentTable,
    payload: Record<string, unknown>,
): Promise<Record<string, unknown>> {
    const result = table === 'care_residents'
        ? await supabase
            .from('care_residents')
            .insert(payload)
            .select(CARE_RESIDENTS_SELECT)
            .single()
        : await supabase
            .from('residents')
            .insert(payload)
            .select(RESIDENTS_SELECT)
            .single();

    const { data, error } = result;
    if (error) throw new Error(error.message);
    return data as Record<string, unknown>;
}

export async function getResidents(facilityId: string, options?: ResidentQueryOptions): Promise<Resident[]> {
    const rows = await collectResidentRows({ facilityId });
    const [sharedAllergies, sharedDiagnoses, practiceLinks, sharedBloodTypes, sharedChronicConditions] = await Promise.all([
        getSharedAllergiesByResidentId(rows),
        getSharedDiagnosesByResidentId(rows),
        getLinkedPracticeInfoByResidentId(rows),
        getSharedBloodTypeByResidentId(rows),
        getSharedChronicConditionsByResidentId(rows),
    ]);

    return applyResidentFilters(
        mergeResidentRows(rows, sharedAllergies, sharedDiagnoses, practiceLinks, sharedBloodTypes, sharedChronicConditions),
        options,
    );
}

export async function getResidentsByIds(ids: string[]): Promise<Resident[]> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
    if (uniqueIds.length === 0) return [];

    const rows = await collectResidentRows({ ids: uniqueIds });
    const [sharedAllergies, sharedDiagnoses, practiceLinks, sharedBloodTypes, sharedChronicConditions] = await Promise.all([
        getSharedAllergiesByResidentId(rows),
        getSharedDiagnosesByResidentId(rows),
        getLinkedPracticeInfoByResidentId(rows),
        getSharedBloodTypeByResidentId(rows),
        getSharedChronicConditionsByResidentId(rows),
    ]);

    return mergeResidentRows(rows, sharedAllergies, sharedDiagnoses, practiceLinks, sharedBloodTypes, sharedChronicConditions);
}

export async function getResidentProfile(residentId: string): Promise<ResidentProfile | null> {
    const [resident] = await getResidentsByIds([residentId]);
    return resident ? mapResidentToProfile(resident) : null;
}

export async function getResidentChartReadModel(
    residentId: string,
    options?: ResidentChartReadOptions,
): Promise<ResidentChartReadModel | null> {
    const resident = await getResidentProfile(residentId);
    if (!resident) {
        return null;
    }

    const include = {
        medications: options?.include?.medications ?? true,
        wounds: options?.include?.wounds ?? true,
        alerts: options?.include?.alerts ?? true,
        contacts: options?.include?.contacts ?? true,
    };
    const facilityId = options?.facilityId ?? resident.facilityId;

    const [medications, wounds, alerts, contacts] = await Promise.all([
        include.medications ? fetchResidentMedications(residentId) : Promise.resolve([]),
        include.wounds && facilityId ? fetchResidentWounds(facilityId, residentId) : Promise.resolve([]),
        include.alerts ? fetchResidentAlerts(residentId) : Promise.resolve([]),
        include.contacts ? getContactsByResident(residentId) : Promise.resolve([]),
    ]);

    return {
        resident,
        medications,
        wounds,
        alerts,
        contacts,
    };
}

export async function createResident(facilityId: string, input: CreateResidentInput): Promise<Resident> {
    const targetTable = await getPreferredResidentTable(facilityId);
    const normalizedWard = input.ward?.trim() || null;
    const normalizedAdmissionDate = input.admissionDate ?? new Date().toISOString().split('T')[0] ?? null;
    const basePayload = {
        facility_id: facilityId,
        first_name: input.firstName,
        last_name: input.lastName,
        date_of_birth: input.dateOfBirth || null,
        gender: input.gender || null,
        room_number: input.roomNumber || null,
        care_level: input.careLevel ?? null,
        primary_diagnosis: input.primaryDiagnosis || null,
        admission_date: normalizedAdmissionDate,
        status: input.status ?? 'active',
        allergies: input.allergies ?? [],
        fall_risk: input.fallRisk ?? false,
        secondary_diagnoses: input.secondaryDiagnoses ?? [],
        decubitus_risk: input.decubitusRisk ?? null,
    };

    const insertPayload = targetTable === 'care_residents'
        ? { ...basePayload, station: normalizedWard, ward: normalizedWard }
        : { ...basePayload, ward: normalizedWard };

    return mapResidentRow(await insertResidentRecord(targetTable, insertPayload));
}

export async function updateResidentRoomAssignment(
    residentId: string,
    params: { roomNumber: string | null; ward?: string | null; bedNumber?: string | null },
): Promise<void> {
    const carePayload = {
        room_number: params.roomNumber,
        ward: params.ward,
        station: params.ward,
        bed_number: params.bedNumber,
    };
    const residentPayload = {
        room_number: params.roomNumber,
        ward: params.ward,
    };

    const [careResult, residentResult] = await Promise.all([
        supabase.from('care_residents').update(carePayload).eq('id', residentId),
        supabase.from('residents').update(residentPayload).eq('id', residentId),
    ]);

    if (careResult.error) throw new Error(careResult.error.message);
    if (residentResult.error) throw new Error(residentResult.error.message);
}

export async function searchAssignableResidents(
    facilityId: string,
    search: string,
    limit = 20,
): Promise<Array<{ id: string; firstName: string; lastName: string; careLevel: number | null }>> {
    const [residents, roomsResult] = await Promise.all([
        getResidents(facilityId, { statuses: ['active'], search }),
        supabase
            .from('facility_rooms')
            .select('id')
            .eq('facility_id', facilityId)
            .eq('is_active', true),
    ]);

    if (roomsResult.error) {
        throw new Error(roomsResult.error.message);
    }

    const roomIds = (roomsResult.data ?? [])
        .map((row) => row.id as string | null)
        .filter((roomId): roomId is string => Boolean(roomId));

    if (roomIds.length === 0) {
        return residents
            .slice(0, limit)
            .map((resident) => ({
                id: resident.id,
                firstName: resident.firstName,
                lastName: resident.lastName,
                careLevel: resident.careLevel,
            }));
    }

    const occupiedBedsResult = await supabase
        .from('facility_beds')
        .select('resident_id')
        .in('room_id', roomIds)
        .not('resident_id', 'is', null);

    if (occupiedBedsResult.error) {
        throw new Error(occupiedBedsResult.error.message);
    }

    const occupiedResidentIds = new Set(
        (occupiedBedsResult.data ?? [])
            .map((row) => row.resident_id as string | null)
            .filter((residentId): residentId is string => Boolean(residentId)),
    );

    return residents
        .filter((resident) => !occupiedResidentIds.has(resident.id))
        .slice(0, limit)
        .map((resident) => ({
            id: resident.id,
            firstName: resident.firstName,
            lastName: resident.lastName,
            careLevel: resident.careLevel,
        }));
}
